<template>
  <card-container
    :id="pageId"
    :loading-message="spinnerText"
    :is-loading="isLoading"
    :disabled="!isPhoneNumberValid || isLoading"
    :hide-reset="isFamilyDashboard"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      {{ formTitle }}
    </template>

    <template
      v-if="!isFamilyDashboard"
      #content
    >
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Phone Number* </label>
        <phone-number-input
          v-model="phoneNumber"
          class="col-sm-8"
          :page-id="pageId"
          :required="true"
        />
      </div>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import events from '@/events';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { useSwalDialog } from '@/use/swalDialog';
import { usePayment } from '@/use/payment';
import { sendPost } from '@/Utils/api';

const pageId = 'pay-unpaid-rides';

const router = useRouter();

const { customerPhoneNumber } = useCustomer();
const { isFamilyDashboard } = useDashboard();
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { unpaidRidesPhoneNumber, setUnpaidRidesPhoneNumber } = usePayment();

const phoneNumber = ref('');
const isLoading = ref(false);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(phoneNumber.value));

const formTitle = computed(() => (isFamilyDashboard.value ? 'Pay Outstanding Balances' : 'Pay for Any Unpaid Rides'));

const submitBtnText = computed(() => (isFamilyDashboard.value ? 'Pay Outstanding Balances' : 'Pay for Any Unpaid Rides'));

const spinnerText = computed(() => (isFamilyDashboard.value ? 'Paying Outstanding Balances...' : 'Paying Unpaid Rides...'));

const submitForm = async () => {
  isLoading.value = true;
  try {
    const response = await sendPost('payForAnyUnpaidRides', { phoneNumber: phoneNumber.value });

    await showSuccessPopupDialog({
      text: response.data.message,
    });

    // if caller is same with form and right panel, refresh right panel
    if (phoneNumber.value === customerPhoneNumber.value) {
      events.emit('refresh-right-panel');
    }

    await checkCanceledScheduledRides(response.data?.canceledScheduledRides || 0);
  } catch (error) {
    const errorText = util.getErrorResponse(error);
    showErrorPopupDialog({
      text: errorText,
      type: 'html',
    });
  }
  isLoading.value = false;

  setUnpaidRidesPhoneNumber('');
};

const checkCanceledScheduledRides = async (canceledScheduledRides: number) => {
  if (!canceledScheduledRides) {
    return;
  }

  const message = isFamilyDashboard.value
    ? 'You have scheduled ride(s) that were auto canceled due to unpaid rides, please check if you would like to reactivate them.'
    : 'This user had scheduled ride(s) that were auto canceled due to unpaid rides, please ask if the user would like to reactivate these scheduled ride(s).';

  const isScheduledRideCheck = await showConfirmPopupDialog({
    text: message,
    title: 'Re-activate Canceled Scheduled Ride(s)?',
    confirmButtonText: 'Check Scheduled Rides',
    cancelButtonText: 'Cancel',
  });

  if (!isScheduledRideCheck) {
    return;
  }

  if (isFamilyDashboard.value) {
    router.push({
      path: `/family/scheduled-rides`,
    });
  } else {
    router.push({
      path: `/operator/scheduled-rides`,
      query: {
        phoneNumber: phoneNumber.value,
      },
    });
  }
};

const resetForm = () => {
  phoneNumber.value = customerPhoneNumber.value || '';
};

watch(customerPhoneNumber, resetForm, {
  immediate: true,
});

watch(
  unpaidRidesPhoneNumber,
  (value: string) => {
    if (value) {
      phoneNumber.value = value;
      submitForm();
    }
  },
  {
    immediate: true,
  },
);
</script>
